import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SiteNav } from "@/components/SiteNav";
import { Logo } from "@/components/Logo";
import { Reveal, SimBadge } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { AreaChart, Area, ResponsiveContainer, Tooltip } from "recharts";
import {
  ArrowRight, ShieldCheck, Activity, TrendingUp, CheckCircle2, Star, Linkedin, Twitter, Mail, Quote,
} from "lucide-react";

// Chart tooltip follows the light/dark theme through the CSS colour tokens.
const tipStyle = { borderRadius: 12, border: "1px solid hsl(var(--border))", fontSize: 12, backgroundColor: "hsl(var(--popover))", color: "hsl(var(--popover-foreground))" };

// Simulated aggregate client-growth curve (a representative $100k account).
const GROWTH = Array.from({ length: 24 }, (_, i) => ({
  m: i,
  value: 100000 + i * 3600 + Math.sin(i / 2) * 5000 + (i > 12 ? (i - 12) * 2400 : 0),
}));

const TRUST_STATS = [
  { label: "Simulated assets tracked", value: "$120M+" },
  { label: "Investors onboarded", value: "300+" },
  { label: "Avg. annual growth (sim.)", value: "+18.4%" },
  { label: "Capital protection first", value: "Always" },
];

const FIRMS = [
  "Cedar Family Office", "Harbor Capital", "Meridian Partners",
  "Northwind Advisors", "Summit Wealth", "Vertex Trading",
];

const REVIEWS = [
  { quote: "MentorAI's discipline is exactly what we look for — steady, transparent, and no drama. We always know where our capital stands.", name: "Elena Cardoso", title: "Managing Partner, Cedar Family Office" },
  { quote: "The clearest reporting we've seen from any manager. Risk-first isn't a slogan here, it's how they actually operate.", name: "Marcus Bloom", title: "CIO, Harbor Capital" },
  { quote: "Calm, consistent, and refreshingly honest about what the system does and doesn't do. Our clients love the updates.", name: "Priya Nair", title: "Principal, Meridian Partners" },
];

const PILLARS = [
  { icon: ShieldCheck, title: "Risk-first", desc: "We protect your capital before we chase any return." },
  { icon: Activity, title: "Disciplined", desc: "We only act on high-confidence opportunities — patience by design." },
  { icon: CheckCircle2, title: "Transparent", desc: "Clear, simple reporting so you always know where your money stands." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", firm: "", aum: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please provide your name and email."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads", form);
      toast.success(data.message || "Request received.");
      setForm({ name: "", email: "", firm: "", aum: "", message: "" });
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-background">
      <SiteNav />

      {/* HERO */}
      <section className="relative overflow-hidden aurora grain">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 sm:pt-28 sm:pb-32">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <Reveal>
                <Badge variant="outline" className="rounded-full bg-white/70 border-black/10 dark:bg-white/5 dark:border-white/10 text-muted-foreground mb-6">
                  <span className="size-1.5 rounded-full bg-brand mr-2" /> Private investment management
                </Badge>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
                  Your money,
                  <br />
                  <span className="text-brand">grown with discipline.</span>
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
                  {BRAND.name} manages capital with a simple promise: protect it first, grow it patiently, and keep you clearly informed every step of the way.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Button data-testid="hero-request-access-button" size="lg" className="rounded-xl bg-[#0EA5A4] hover:bg-[#0B8E8D] text-white h-12 px-6" onClick={() => scrollTo("request-access")}>
                    Request access <ArrowRight className="ml-2 size-4" />
                  </Button>
                  <Button data-testid="hero-login-button" size="lg" variant="outline" className="rounded-xl h-12 px-6 bg-white dark:bg-white/5 dark:hover:bg-white/10" onClick={() => navigate("/login")}>
                    Client login
                  </Button>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-8 flex items-center gap-6 text-sm text-muted-foreground">
                  <span className="flex items-center gap-2"><ShieldCheck className="size-4 text-brand" /> Risk-first</span>
                  <span className="flex items-center gap-2"><TrendingUp className="size-4 text-brand" /> Patient growth</span>
                </div>
              </Reveal>
            </div>

            {/* Glass growth card */}
            <Reveal delay={0.15}>
              <Card className="relative rounded-2xl glass ring-1 ring-black/5 dark:ring-white/10 shadow-[0_20px_60px_rgba(10,12,15,0.12)] dark:shadow-[0_20px_60px_rgba(0,0,0,0.5)] p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Client growth</div>
                    <div className="font-display text-2xl font-semibold">+38.2% <span className="text-sm text-muted-foreground font-normal">simulated</span></div>
                  </div>
                  <SimBadge />
                </div>
                <div className="h-44 -mx-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={GROWTH} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="hg" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0EA5A4" stopOpacity={0.28} />
                          <stop offset="100%" stopColor="#0EA5A4" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Area type="monotone" dataKey="value" stroke="#0EA5A4" strokeWidth={2.4} fill="url(#hg)" />
                      <Tooltip formatter={(v) => [`$${Math.round(v).toLocaleString()}`, "Value"]} labelFormatter={() => ""} contentStyle={tipStyle} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {[{ l: "Since inception", v: "+38.2%" }, { l: "Best month", v: "+6.1%" }, { l: "Worst month", v: "−2.3%" }].map((s) => (
                    <div key={s.l} className="rounded-xl bg-white/70 dark:bg-white/5 ring-1 ring-black/5 dark:ring-white/10 p-3">
                      <div className="text-[11px] text-muted-foreground">{s.l}</div>
                      <div className="font-display text-base font-semibold font-mono-num">{s.v}</div>
                    </div>
                  ))}
                </div>
              </Card>
            </Reveal>
          </div>
        </div>
      </section>

      {/* GROWTH */}
      <section id="growth" className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <div className="flex items-center gap-3 mb-4">
                <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">How we've grown our clients' money</h2>
              </div>
              <p className="text-muted-foreground text-lg leading-relaxed">
                A representative client account, grown patiently over time. Steady compounding matters more than chasing every move — and our first job is never losing it.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4">
                {TRUST_STATS.map((s) => (
                  <Card key={s.label} className="rounded-2xl p-5 ring-1 ring-black/5 dark:ring-white/10">
                    <div className="font-display text-2xl font-semibold font-mono-num">{s.value}</div>
                    <div className="mt-1 text-sm text-muted-foreground">{s.label}</div>
                  </Card>
                ))}
              </div>
              <p className="mt-4 text-xs text-muted-foreground">Figures shown are illustrative &amp; simulated during our revalidation stage.</p>
            </Reveal>
            <Reveal delay={0.1}>
              <Card className="rounded-2xl p-6 ring-1 ring-black/5 dark:ring-white/10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-sm text-muted-foreground">Representative account value</h3>
                  <SimBadge />
                </div>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={GROWTH} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gg" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0EA5A4" stopOpacity={0.26} />
                          <stop offset="100%" stopColor="#0EA5A4" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Area type="monotone" dataKey="value" stroke="#0EA5A4" strokeWidth={2.6} fill="url(#gg)" />
                      <Tooltip formatter={(v) => [`$${Math.round(v).toLocaleString()}`, "Value"]} labelFormatter={() => ""} contentStyle={tipStyle} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FIRMS */}
      <section id="firms" className="py-16 bg-secondary/40 border-y">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <p className="text-sm font-medium text-muted-foreground tracking-wide uppercase">Trusted by leading firms &amp; family offices</p>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
              {FIRMS.map((f) => (
                <span key={f} className="font-display text-lg sm:text-xl font-semibold text-foreground/40 hover:text-foreground/70 transition-colors">{f}</span>
              ))}
            </div>
          </Reveal>
          <p className="mt-6 text-xs text-muted-foreground">Firm names shown are illustrative placeholders.</p>
        </div>
      </section>

      {/* REVIEWS */}
      <section id="reviews" className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">What our clients say</h2>
            <p className="mt-4 text-muted-foreground text-lg">Relationships built on clarity, patience, and trust.</p>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {REVIEWS.map((r, i) => {
              const initials = r.name.split(" ").map((s) => s[0]).slice(0, 2).join("");
              return (
                <Reveal key={r.name} delay={i * 0.06}>
                  <Card data-testid={`review-${i}`} className="rounded-2xl p-6 h-full ring-1 ring-black/5 dark:ring-white/10 flex flex-col">
                    <Quote className="size-6 text-brand/40" />
                    <div className="mt-3 flex gap-0.5">
                      {[...Array(5)].map((_, s) => <Star key={s} className="size-3.5 fill-amber-400 text-amber-400" />)}
                    </div>
                    <p className="mt-4 text-sm leading-relaxed text-foreground flex-1">"{r.quote}"</p>
                    <div className="mt-5 flex items-center gap-3">
                      <Avatar className="size-9"><AvatarFallback className="bg-brand/10 text-brand text-xs">{initials}</AvatarFallback></Avatar>
                      <div>
                        <div className="text-sm font-medium">{r.name}</div>
                        <div className="text-xs text-muted-foreground">{r.title}</div>
                      </div>
                    </div>
                  </Card>
                </Reveal>
              );
            })}
          </div>
          <p className="mt-6 text-xs text-muted-foreground">Reviews shown are illustrative placeholders.</p>
        </div>
      </section>

      {/* APPROACH */}
      <section id="about" className="py-20 sm:py-28 bg-secondary/40 border-t">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Our approach, in three words</h2>
            <p className="mt-4 text-muted-foreground text-lg">You don't need to follow every trade. You just need to trust how it's handled.</p>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-3 gap-5">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <Card className="rounded-2xl p-6 h-full bg-white dark:bg-card ring-1 ring-black/5 dark:ring-white/10">
                  <div className="size-10 rounded-xl bg-brand/10 flex items-center justify-center text-brand"><p.icon className="size-5" /></div>
                  <h3 className="mt-4 font-medium">{p.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REQUEST ACCESS */}
      <section id="request-access" className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Request client access</h2>
            <p className="mt-4 text-muted-foreground text-lg">Tell us a little about you. Access to the client portal is granted after a quick review.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <Card className="mt-10 rounded-2xl p-6 sm:p-8 ring-1 ring-black/5 dark:ring-white/10">
              <form data-testid="request-access-form" onSubmit={submit} className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label>Full name</Label>
                  <Input data-testid="lead-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input data-testid="lead-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@firm.com" />
                </div>
                <div className="space-y-2">
                  <Label>Firm (optional)</Label>
                  <Input data-testid="lead-firm-input" value={form.firm} onChange={(e) => setForm({ ...form, firm: e.target.value })} placeholder="Firm / family office" />
                </div>
                <div className="space-y-2">
                  <Label>Investable capital (optional)</Label>
                  <Select value={form.aum} onValueChange={(v) => setForm({ ...form, aum: v })}>
                    <SelectTrigger data-testid="lead-aum-select"><SelectValue placeholder="Select range" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="<250k">Under $250k</SelectItem>
                      <SelectItem value="250k-1m">$250k – $1M</SelectItem>
                      <SelectItem value="1m-5m">$1M – $5M</SelectItem>
                      <SelectItem value="5m+">$5M+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>Message (optional)</Label>
                  <Textarea data-testid="lead-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="What are you looking for?" rows={4} />
                </div>
                <div className="sm:col-span-2 flex items-center justify-between gap-4 flex-wrap">
                  <p className="text-xs text-muted-foreground">Access requires approval by our team.</p>
                  <Button data-testid="request-access-submit-button" type="submit" disabled={submitting} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0B8E8D] text-white h-11 px-6">
                    {submitting ? "Sending…" : "Submit request"}
                  </Button>
                </div>
              </form>
            </Card>
          </Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t bg-white dark:bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row justify-between gap-10">
            <div className="max-w-sm">
              <Logo />
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{BRAND.tagline} Private investment management, built risk-first for individuals, family offices, and firms.</p>
              <div className="mt-5 flex items-center gap-3">
                <a href={BRAND.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="size-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/70 transition-colors"><Linkedin className="size-4" /></a>
                <a href={BRAND.social.x} target="_blank" rel="noopener noreferrer" aria-label="X" className="size-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/70 transition-colors"><Twitter className="size-4" /></a>
                <a href={`mailto:${BRAND.contactEmail}`} aria-label="Email" className="size-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/70 transition-colors"><Mail className="size-4" /></a>
              </div>
            </div>
            <div className="flex gap-10 sm:gap-16 flex-wrap">
              <div>
                <div className="text-sm font-medium mb-3">Explore</div>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li><a href="#growth" className="hover:text-foreground">How we grow</a></li>
                  <li><a href="#reviews" className="hover:text-foreground">Reviews</a></li>
                  <li><a href="#about" className="hover:text-foreground">Our approach</a></li>
                </ul>
              </div>
              <div>
                <div className="text-sm font-medium mb-3">Access</div>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li><button onClick={() => navigate("/login")} className="hover:text-foreground">Client login</button></li>
                  <li><a href="#request-access" className="hover:text-foreground">Request access</a></li>
                </ul>
              </div>
              <div>
                <div className="text-sm font-medium mb-3">Contact</div>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li><a href={`mailto:${BRAND.accessEmail}`} className="hover:text-foreground">{BRAND.accessEmail}</a></li>
                  <li><a href={`mailto:${BRAND.contactEmail}`} className="hover:text-foreground">{BRAND.contactEmail}</a></li>
                  <li>{BRAND.location}</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t text-xs text-muted-foreground leading-relaxed space-y-3">
            <p><strong className="text-foreground/80">Important disclosures.</strong> {BRAND.legalEntity} ({BRAND.location}). All performance figures, growth charts, firm names, and reviews shown are <strong>ILLUSTRATIVE and SIMULATED</strong> during MentorAI's revalidation stage; they are not a record of live trading, investment advice, an offer or solicitation, or a promise of returns. Investing involves substantial risk of loss.</p>
            <p className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span>© {new Date().getFullYear()} {BRAND.legalEntity}. All rights reserved.</span>
              <span className="opacity-60">Terms</span>
              <span className="opacity-60">Privacy</span>
              <span className="opacity-60">Risk Disclosure</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
